// ReplyPreview.js - Shows quoted/replied message with click support for scrolling
import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Reply as ReplyIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

export default function ReplyPreview({ 
  replyToMessage, 
  isQuote = false, 
  onCancel,
  compact = false,
  onClick, // NEW: Click handler for scrolling to original message
  clickable = false // NEW: Whether this preview is clickable
}) {
  const theme = useTheme();

  if (!replyToMessage) return null;

  const truncateMessage = (text, maxLength = compact ? 50 : 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getSenderName = (message) => {
    if (message.sender) {
      return `${message.sender.first_name || ''} ${message.sender.last_name || ''}`.trim();
    }
    if (message.creator_id) {
      return message.creator_id;
    }
    return 'System';
  };

  const getSenderAvatar = (message) => {
    if (message.sender?.phphref) return message.sender.phphref;
    if (message.creator?.pfphref) return message.creator.pfphref;
    return null;
  };

  const handleClick = (e) => {
    if (clickable && onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick();
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        mb: 0.5,
        px: compact ? 0.75 : 1,
        py: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        borderLeft: `3px solid ${theme.palette.primary.main}`,
        borderRadius: '6px 6px 6px 6px',
        position: 'relative',
        cursor: clickable ? 'pointer' : 'default',
        transition: 'all 0.15s ease',
        maxWidth: '100%',

        ...(clickable && {
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.1),
          },
        })
      }}
    >
      {/* Header with sender name, reply icon, and cancel button */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
          <ReplyIcon sx={{ fontSize: 12, color: 'primary.main', flexShrink: 0 }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              fontSize: '0.65rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {getSenderName(replyToMessage)}
          </Typography>
        </Box>
        
        {/* Cancel button */}
        {onCancel && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
            sx={{
              p: 0.25,
              color: 'text.secondary',
              '&:hover': {
                color: 'error.main',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>

      {/* Message content */}
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          fontSize: '0.7rem',
          lineHeight: 1.3,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: compact ? 1 : 2,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-word',
        }}
      >
        {replyToMessage.attachments ? '📎 Attachment' : (replyToMessage.message || '')}
      </Typography>
    </Box>
  );
}